import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { ShowHeader } from "@/common/components/ShowHeader";
import TextField from "@/common/components/TextField";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { SubmitHandler, useForm } from "react-hook-form";
import { objectiveSystemsSchema, ObjectiveSystemsValidationSchema } from "../../schemas/objectiveSystems.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import useCreateObjectiveSystem from "../../hooks/useCreateMaintenanceObjectiveSystem";
import { useGoBack } from "@/common/hooks/useGoBack";
import { useState } from "react";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { FormTransformers } from "@/common/utils/formTransformers";
import { useQueryClient } from "@tanstack/react-query";

const MaintenanceObjectiveSystemsCreate = () => {
  const goBack = useGoBack();

  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions();

  const {
    mutate: createObjectiveSystem,
    isPending: isCreateObjectiveSystemPending,
  } = useCreateObjectiveSystem({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenanceObjectiveSystems'] });
      goBack({ fallback: `/objective-maintenance/systems` });
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError, setMessages })(error);
    },
  })

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ObjectiveSystemsValidationSchema>({
    resolver: zodResolver(objectiveSystemsSchema),
  });

  const onSubmit: SubmitHandler<ObjectiveSystemsValidationSchema> = (data) => createObjectiveSystem(data);

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-64px)]"
      statusCode={
        hasUserPermission('Admin::Maintenance::ObjectiveSystems::Create') || isUserPermissionsPending
          ? 200
          : 403
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <ShowHeader
          goBack
          goBackProps={{ fallback: `/objective-maintenance/systems` }}
          titleClassName="text-nowrap max-w-56 overflow-hidden text-ellipsis font-normal text-base"
          title="Objetos de manutenção"
          subtitle="Novo Sistema"
          buttons={(
            <div className="flex items-center gap-3">
              <Button
                variant="outline-error"
                onClick={() => goBack({ fallback: `/objective-maintenance/systems` })}
                disabled={isCreateObjectiveSystemPending}
              >
                Cancelar
              </Button>

              <Button
                type="submit"
                name="submit"
                isLoading={isCreateObjectiveSystemPending}
              >
                Criar sistema
              </Button>
            </div>
          )}
        />

        <Box className="max-h-[calc(100vh-132px)] overflow-y-auto flex flex-col gap-4">
          <p className="text-lg font-medium">
            Dados gerais do sistema
          </p>

          <TextField
            label="Nome do sistema"
            maxLength={32}
            {...register('display_name')}
            disabled={isCreateObjectiveSystemPending}
            errorMessage={errors.display_name?.message || undefined}
            markAsRequired
          />
        </Box>
      </form>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </ErrorMessageWrapper>
  );
}

export default MaintenanceObjectiveSystemsCreate;
